import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ChevronRight, Star } from "lucide-react";

/**
 * FriendsFavoritedCarousel — cold-start discovery row on the Directory page.
 *
 * For signed-in customers only. Shows businesses ≥1 of their friends
 * favorited in the last 30 days. Each card carries an inline stack of the
 * friend avatars that favorited it. Renders nothing when the customer has
 * zero friend-favorites — no empty state, so the directory stays clean.
 */
function Avatar({ url, name, size = 24 }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="rounded-full object-cover border-2 border-white shadow-sm"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center border-2 border-white shadow-sm"
      style={{ width: size, height: size, fontSize: size / 3 }}
    >
      {initials}
    </div>
  );
}

function BusinessCard({ b }) {
  const friendPreview = b.friends || [];
  const summary = (() => {
    const names = friendPreview.map((f) => f.name).filter(Boolean);
    if (b.friend_favorite_count === 1) return `${names[0] || "A friend"} favorited`;
    if (b.friend_favorite_count === 2) return `${names[0]} and ${names[1] || "a friend"} favorited`;
    return `${names[0]}, ${names[1] || "a friend"} and ${b.friend_favorite_count - 2} other${b.friend_favorite_count - 2 === 1 ? "" : "s"} favorited`;
  })();

  return (
    <Link
      to={`/businesses/${b.id}`}
      className="group shrink-0 w-[260px] snap-start bg-white border border-border rounded-md p-4 hover:shadow-md hover:border-[#B31E1E]/30 transition-all"
      data-testid={`friends-fav-card-${b.id}`}
    >
      <div className="flex items-center gap-3">
        {b.logo_data_url ? (
          <img
            src={b.logo_data_url}
            alt=""
            className="h-12 w-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center shrink-0 text-lg">
            {(b.business_name || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate group-hover:text-[#B31E1E] transition-colors">
            {b.business_name}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {b.category || "—"}
            {b.rating_count > 0 && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-gray-400">
                <Star size={9} className="text-amber-500" fill="currentColor" />
                {(b.rating_avg || 0).toFixed(1)}
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 truncate">
            {[b.business_city, b.business_state].filter(Boolean).join(", ")}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex -space-x-2 shrink-0">
          {friendPreview.map((f) => (
            <Avatar key={f.id} url={f.avatar_data_url} name={f.name} />
          ))}
        </div>
        <div className="min-w-0 flex-1 text-[11px] text-rose-700 leading-tight inline-flex items-center gap-1">
          <Heart size={10} className="text-rose-500 shrink-0" fill="currentColor" />
          <span className="truncate">{summary}</span>
        </div>
      </div>
    </Link>
  );
}

export default function FriendsFavoritedCarousel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user || user.role !== "customer") {
      setLoading(false);
      return () => {};
    }
    (async () => {
      try {
        const { data } = await api.get("/discovery/friends-favorited-recent");
        if (!cancelled) setItems(data?.businesses || []);
      } catch {
        // silent — social proof is best-effort
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading || !user || user.role !== "customer" || items.length === 0) {
    return null;
  }

  return (
    <section
      className="mt-8 mb-4 rounded-md border border-rose-100 bg-rose-50/40 p-5"
      data-testid="friends-favorited-carousel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Heart size={16} className="text-rose-500" fill="currentColor" />
        <h2 className="font-display text-xl">Recently favorited by friends</h2>
        <span className="text-[11px] font-mono text-rose-600 bg-rose-100 rounded-full px-2 py-0.5">
          LAST 30 DAYS
        </span>
        <span className="ml-auto text-xs text-gray-500 hidden sm:flex items-center gap-1">
          Discover through your circle <ChevronRight size={12} />
        </span>
      </div>

      <div className="flex overflow-x-auto gap-3 pb-1 snap-x snap-mandatory scrollbar-thin">
        {items.map((b) => (
          <BusinessCard key={b.id} b={b} />
        ))}
      </div>
    </section>
  );
}
